import { useCallback, useMemo } from 'react';
import { useActor, useSelector } from '@xstate/react';

import { useGlobalAppState } from '@order/GlobalAppState';

import type { CartMutation } from './Cart.machine.model';
import { cartModel } from './Cart.machine.model';
import { getPendingMutationsNames } from './Cart.machine.utils';

// ────────────────────────────────────────────────────────────────────────────────

export const useCartMachine = () => {
  const { cartMachineRef: cartService } = useGlobalAppState();
  const [state, send] = useActor(cartService);

  const shouldDisableCartControls = useSelector(cartService, (currentState) =>
    currentState.matches('controls.disabled'),
  );
  const pendingCartMutations = getPendingMutationsNames(state.context);

  // ─── HELPERS ─────────────────────────────────────────────────────

  /**
   * Tracks pending cart mutation's fetching state.
   *
   * @example
   * const addLineItemResult = await executeCartMutation(addLineItemMutation);
   */
  const executeCartMutation = useCallback<ExecuteCartMutation>(
    async (mutation, mutationName) => {
      const { events } = cartModel;
      const { REGISTER_PENDING_MUTATION, UNREGISTER_PENDING_MUTATION } = events;

      // ------ ------ ------ ------ ------ ------

      send(REGISTER_PENDING_MUTATION(mutationName));
      const result = await mutation();

      send(UNREGISTER_PENDING_MUTATION(mutationName));

      // ------ ------ ------ ------ ------ ------

      return result;
    },
    [send],
  );

  // ─────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      executeCartMutation,
      pendingCartMutations,
      shouldDisableCartControls,
    }),
    [executeCartMutation, pendingCartMutations, shouldDisableCartControls],
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type ExecuteCartMutation = <DataType>(
  mutation: () => Promise<DataType>,
  mutationName: CartMutation,
) => Promise<DataType>;
